/*
 * a11y-dialog.js — shared focus management for the theme's custom overlays
 * (cart drawer, compare drawer, quote dialog, quick-add modal, mobile menu).
 *
 * Each overlay keeps its own open/close logic and simply calls:
 *   window.A11yDialog.activate(panelEl, { onEscape })   // on open
 *   window.A11yDialog.deactivate(panelEl)               // on close
 *
 * activate() moves focus into the panel, traps Tab within it, and (optionally)
 * closes on Escape. deactivate() releases the trap and restores focus to
 * whatever was focused when the panel opened (typically the trigger control).
 *
 * WCAG 2.4.3 (Focus Order), 2.1.2 (No Keyboard Trap — i.e. Tab stays inside a
 * modal but Escape/close always exits), 4.1.2.
 */
(function () {
  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
    ' select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]),' +
    ' [contenteditable="true"]';

  // panel element -> { previouslyFocused, onKeydown }
  var active = new Map();

  function visibleFocusable(container) {
    return Array.prototype.slice
      .call(container.querySelectorAll(FOCUSABLE))
      // offsetParent is null for display:none subtrees (e.g. hidden submenu levels)
      .filter(function (el) {
        return el.offsetParent !== null || el.getClientRects().length > 0;
      });
  }

  function activate(container, opts) {
    opts = opts || {};
    if (!container || active.has(container)) return;

    var previouslyFocused = document.activeElement;

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (typeof opts.onEscape === 'function') {
          e.preventDefault();
          opts.onEscape();
        }
        return;
      }
      if (e.key !== 'Tab') return;

      var focusable = visibleFocusable(container);
      if (focusable.length === 0) {
        e.preventDefault();
        if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
        container.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var current = document.activeElement;

      if (!container.contains(current)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && current === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // Capture phase so the trap runs before per-widget handlers.
    document.addEventListener('keydown', onKeydown, true);
    active.set(container, { previouslyFocused: previouslyFocused, onKeydown: onKeydown });

    // Move focus in once the panel has painted (overlays animate in).
    var delay = typeof opts.focusDelay === 'number' ? opts.focusDelay : 60;
    setTimeout(function () {
      var focusable = visibleFocusable(container);
      var target = null;
      if (opts.initialFocus) target = container.querySelector(opts.initialFocus);
      if (!target) target = focusable[0] || container;
      if (target === container && !container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '-1');
      }
      try { target.focus({ preventScroll: true }); } catch (err) { /* noop */ }
    }, delay);
  }

  function deactivate(container, opts) {
    opts = opts || {};
    var state = active.get(container);
    if (!state) return;
    document.removeEventListener('keydown', state.onKeydown, true);
    active.delete(container);

    var restore = opts.restoreFocus || state.previouslyFocused;
    if (restore && typeof restore.focus === 'function' && document.contains(restore)) {
      // Delay so focus lands after the closing animation removes the panel.
      setTimeout(function () {
        try { restore.focus({ preventScroll: true }); } catch (err) { /* noop */ }
      }, typeof opts.focusDelay === 'number' ? opts.focusDelay : 0);
    }
  }

  window.A11yDialog = { activate: activate, deactivate: deactivate };
})();
